import { useEffect, useState } from "react";
import { format, addDays } from "date-fns";
import { toast } from "sonner";
import { Video, Phone, Building2, CheckCircle2, Clock, Loader2, CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { http } from "@/lib/api";

const MODES = [
  { id: "video", label: "Video Call", icon: Video },
  { id: "phone", label: "Phone Call", icon: Phone },
  { id: "factory", label: "Office Visit", icon: Building2 },
];
const TOPICS = [
  "Private Label Launch",
  "Custom Formulation (R&D)",
  "Packaging & Branding",
  "Export / Distribution",
  "PLI Events",
];
const EMPTY = { name: "", email: "", phone: "", company: "", notes: "" };

function useSlots(date) {
  const [slots, setSlots] = useState(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!date) return;
    setSlots(null);
    http
      .get("/meetings/slots", { params: { date: format(date, "yyyy-MM-dd") } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]));
  }, [date, nonce]);
  return [slots, () => setNonce((n) => n + 1)];
}

function SlotGrid({ slots, value, onChange }) {
  if (!slots) return <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#A68840]" /></div>;
  if (!slots.some((s) => s.available))
    return <p data-testid="meeting-no-slots" className="text-sm text-[#556B57] py-6">No slots left on this day — please pick another date.</p>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="meeting-slots">
      {slots.map((s) => (
        <button
          key={s.time}
          type="button"
          disabled={!s.available}
          onClick={() => onChange(s.time)}
          data-testid={`meeting-slot-${s.time.replace(":", "")}`}
          className={`h-11 rounded-full text-sm border transition-[background-color,color,border-color,transform] duration-300 ${
            value === s.time
              ? "bg-[#1D3A2F] text-[#F4E4BD] border-[#1D3A2F] scale-[1.04]"
              : s.available
                ? "bg-white/70 border-[#D7D4CF] text-[#1D3A2F] hover:border-[#C9A961] hover:-translate-y-0.5"
                : "bg-transparent border-dashed border-[#D7D4CF] text-[#222]/25 line-through cursor-not-allowed"
          }`}
        >
          {s.time}
        </button>
      ))}
    </div>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input
        data-testid={testid}
        {...props}
        className="w-full h-12 px-5 rounded-full bg-white/80 border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/20 transition-[border-color,box-shadow]"
      />
    </label>
  );
}

function Confirmation({ booking, onReset }) {
  const m = MODES.find((x) => x.id === booking.mode);
  return (
    <div data-testid="meeting-confirmation" className="text-center py-10 animate-fade-up">
      <div className="w-20 h-20 mx-auto rounded-full bg-[#C9A961]/15 border gold-border flex items-center justify-center animate-glow-pulse">
        <CheckCircle2 className="w-9 h-9 text-[#A68840]" strokeWidth={1.4} />
      </div>
      <h3 className="font-editorial text-4xl text-[#1D3A2F] mt-6">Your meeting is confirmed.</h3>
      <p className="text-[#222]/70 mt-3">
        {m?.label} · {format(new Date(booking.date), "EEEE, d MMMM yyyy")} · {booking.time} IST
      </p>
      <p className="text-sm text-[#556B57] mt-2">Our expert will reach out on your email & phone before the session.</p>
      <button onClick={onReset} data-testid="meeting-book-another" className="mt-8 h-12 px-7 rounded-full border border-[#1D3A2F]/30 text-[#1D3A2F] text-[12px] tracking-[0.18em] uppercase hover:border-[#C9A961] transition-colors">
        Book another
      </button>
    </div>
  );
}

export function BookMeeting() {
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [mode, setMode] = useState("video");
  const [topic, setTopic] = useState(TOPICS[0]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState(null);
  const [slots, refreshSlots] = useSlots(date);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) return toast.error("Please pick a date and time slot.");
    setBusy(true);
    try {
      const { data } = await http.post("/meetings", { ...form, topic, mode, date: format(date, "yyyy-MM-dd"), time });
      setBooking(data);
      toast.success("Meeting booked!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book. Please try again.");
      setTime("");
      refreshSlots();
    } finally {
      setBusy(false);
    }
  };

  if (booking)
    return <Confirmation booking={booking} onReset={() => { setBooking(null); setForm(EMPTY); setTime(""); refreshSlots(); }} />;

  const today = new Date();
  return (
    <form onSubmit={submit} data-testid="book-meeting-form" className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5 space-y-6">
        <div className="eyebrow flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> 1 · Pick a date</div>
        <div className="rounded-3xl bg-white/70 border border-[#D7D4CF]/70 p-2 inline-block" data-testid="meeting-calendar">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => { setDate(d); setTime(""); }}
            disabled={[{ before: today }, { after: addDays(today, 45) }, { dayOfWeek: [0] }]}
          />
        </div>
        <div>
          <div className="eyebrow flex items-center gap-2 mb-3"><Clock className="w-3.5 h-3.5" /> 2 · Pick a time (IST)</div>
          {date ? <SlotGrid slots={slots} value={time} onChange={setTime} /> : <p className="text-sm text-[#556B57]">Select a date to see open slots.</p>}
        </div>
      </div>
      <div className="lg:col-span-7 space-y-5">
        <div className="eyebrow">3 · How should we meet?</div>
        <div className="grid grid-cols-3 gap-3">
          {MODES.map(({ id, label, icon: Icon }) => (
            <button key={id} type="button" onClick={() => setMode(id)} data-testid={`meeting-mode-${id}`}
              className={`rounded-2xl p-4 border text-left transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 ${mode === id ? "bg-[#1D3A2F] border-[#1D3A2F] text-[#F4E4BD]" : "bg-white/70 border-[#D7D4CF] text-[#1D3A2F] hover:border-[#C9A961]"}`}>
              <Icon className="w-5 h-5 mb-3" strokeWidth={1.5} />
              <div className="text-sm font-medium">{label}</div>
            </button>
          ))}
        </div>
        <label className="block">
          <span className="eyebrow block mb-2">Topic</span>
          <select data-testid="meeting-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
            className="w-full h-12 px-5 rounded-full bg-white/80 border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#C9A961]">
            {TOPICS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Name *" testid="meeting-name" required minLength={2} value={form.name} onChange={set("name")} />
          <Input label="Email *" testid="meeting-email" type="email" required value={form.email} onChange={set("email")} />
          <Input label="Phone *" testid="meeting-phone" type="tel" required minLength={6} value={form.phone} onChange={set("phone")} />
          <Input label="Brand / Company" testid="meeting-company" value={form.company} onChange={set("company")} />
        </div>
        <label className="block">
          <span className="eyebrow block mb-2">Anything we should prepare?</span>
          <textarea data-testid="meeting-notes" rows={3} value={form.notes} onChange={set("notes")}
            className="w-full px-5 py-3 rounded-2xl bg-white/80 border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#C9A961] resize-none" />
        </label>
        <div className="flex flex-wrap items-center gap-4 pt-2">
          <button type="submit" disabled={busy} data-testid="meeting-submit"
            className="btn-gold inline-flex items-center gap-3 h-14 px-8 rounded-full text-[12px] tracking-[0.18em] uppercase font-semibold disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" />}
            {busy ? "Booking…" : "Confirm Meeting"}
          </button>
          {date && time && (
            <span data-testid="meeting-summary" className="text-sm text-[#556B57]">
              {format(date, "EEE, d MMM")} · {time} IST · 30 min
            </span>
          )}
        </div>
      </div>
    </form>
  );
}
